import { Button } from '@ipfabric/ui-components/Button/Button';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { LabelTargetType, SnapshotId } from '@ipfabric/utils';
import { ApiTableView } from 'components/ApiTable/components/ApiTableView';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import {
  type UseApiTableProps,
  useApiTable,
} from 'components/ApiTable/model/useApiTable';
import { useDeviceViewContent } from 'components/DeviceView/DeviceViewContentContext';
import { createAssignedByColumn } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/assignedByColumn';
import { useOpenDiscoveryDataEnrichment } from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/useOpenDiscoveryDataEnrichment';
import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { LabelsEditor } from './LabelsEditor';
import { getLabelsTargetFilter } from './labelAssignments';

type LabelsTableProps = {
  active: boolean;
  snapshotId?: SnapshotId | null;
  targetId: string;
  targetType: LabelTargetType;
  title: string;
};

const LABELS_TABLE_COLUMNS = ['name', 'value', 'assignment'] as const;

const useExplorerLabelsTable = (
  targetType: LabelTargetType,
  targetId: string,
  props: Partial<UseApiTableProps>,
) => {
  const openDiscoveryDataEnrichment = useOpenDiscoveryDataEnrichment();
  const endpoint = useTableEndpoint({
    apiEndpoint: '/tables/labels',
    alternative: (base) => ({
      id: `${targetType}-${targetId}-explorer-labels`,
      columns: [
        ...(['target', ...LABELS_TABLE_COLUMNS] as const).map((key) => {
          const column = base.columns.find((item) => item.key === key);
          if (!column) {
            throw new Error(`Missing labels table column: ${key}`);
          }
          return {
            ...column,
            hidden: false,
            title: key === 'assignment' ? 'Source' : column.title,
          };
        }),
        createAssignedByColumn({
          hidden: false,
          linkToPage: true,
          onOpen: openDiscoveryDataEnrichment,
          title: 'Assignment source',
        }),
      ],
    }),
  });

  return useApiTable({
    endpoint,
    disableTableRowDetail: true,
    ...props,
    temporarySettings: true,
  });
};

const BareLabelsTable: FC<LabelsTableProps> = ({
  active,
  snapshotId,
  targetId,
  targetType,
  title,
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  useEffect(() => {
    if (!active) {
      setEditorOpen(false);
    }
  }, [active]);
  const staticFilters = useMemo(
    () => getLabelsTargetFilter(targetType, targetId),
    [targetId, targetType],
  );

  const model = useExplorerLabelsTable(targetType, targetId, {
    snapshotId,
    staticFilters,
  });
  const { onReloadParentTable } = useDeviceViewContent();

  const refreshTables = useStableCallback(() => {
    model.dispatch(reloadTableData);
    onReloadParentTable?.();
  });

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      <div className='shrink-0 bg-white p-sm'>
        <Button
          className='w-fit'
          dataTestId='labels-edit'
          elementProps={{ 'aria-label': 'Edit labels' }}
          icon='fa-tags'
          iconPosition='start'
          onClick={() => setEditorOpen(true)}
          type='button'
          variant='outline'
        >
          Edit labels
        </Button>
      </div>
      <ApiTableView
        borderless
        className='min-h-0 flex-1 overflow-hidden'
        hideTitle
        model={model}
      />
      {active && editorOpen && snapshotId ? (
        <LabelsEditor
          key={`${snapshotId}:${targetType}:${targetId}`}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            refreshTables();
            setEditorOpen(false);
          }}
          snapshotId={snapshotId}
          targetId={targetId}
          targetType={targetType}
          title={title}
        />
      ) : null}
    </div>
  );
};

export const LabelsTable = memo(BareLabelsTable);
